<template>
  <nav
    class="fixed inset-x-0 top-0 z-50 px-3 pt-3 transition-all duration-300 sm:px-5"
  >
    <div class="mx-auto max-w-7xl">
      <div
        class="overflow-hidden border backdrop-blur-xl transition-all duration-300"
        :class="[
          mobileMenu
            ? 'border-black/10 bg-[#f7f4ee]/95 shadow-[0_18px_50px_rgba(31,40,38,0.12)]'
            : isScrolled
            ? 'border-black/10 bg-[#f7f4ee]/82 shadow-[0_18px_50px_rgba(31,40,38,0.10)]'
            : 'border-white/35 bg-[#f7f4ee]/58 shadow-[0_18px_60px_rgba(0,0,0,0.08)]',
          mobileMenu ? 'rounded-[28px]' : 'rounded-full',
        ]"
      >
        <div class="flex h-16 items-center justify-between px-3 sm:px-5 lg:h-[72px] lg:px-6">
          <a
            href="#"
            class="flex min-w-0 items-center gap-2 sm:gap-3"
            aria-label="PHOSPHENE ARCHITECTS home"
            @click.prevent="scrollToView('home')"
          >
            <span
              v-if="logoIcon"
              class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#002140] p-1 sm:h-11 sm:w-11 lg:h-12 lg:w-12"
            >
              <img
                :src="logoIcon"
                alt="PHOSPHENE ARCHITECTS logo"
                class="h-full w-full rounded-lg object-cover"
              />
            </span>
            <img v-if="logoWordmark" :src="logoWordmark" alt="PHOSPHENE ARCHITECTS" class="h-6 max-w-[150px] object-contain sm:h-7 sm:max-w-[250px] lg:h-8 lg:max-w-[300px]" />
            <span v-if="!logoIcon && !logoWordmark" class="truncate font-serif text-xl tracking-[0.08em] text-[#1f2826] sm:text-2xl">
              PHOSPHENE ARCHITECTS
            </span>
          </a>
          <div class="hidden items-center rounded-full border border-black/5 bg-white/50 p-1 md:flex">
            <a
              v-for="nav in navs"
              :key="nav.id"
              @click="scrollToView(nav.name)"
              class="cursor-pointer rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition duration-300 lg:px-5"
              :class="path === nav.id
                ? 'bg-[#23362f] text-[#f7f2e8] shadow-[0_10px_24px_rgba(35,54,47,0.20)]'
                : 'text-[#2f3835] hover:bg-white hover:text-[#101513]'"
              :aria-current="path === nav.id ? 'page' : undefined"
            >
              {{ nav.label }}
            </a>
          </div>

          <div class="flex items-center md:hidden">
            <button
              class="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/70 text-[#1f2826] shadow-sm transition hover:bg-white sm:h-11 sm:w-11"
              type="button"
              aria-label="Toggle menu"
              :aria-expanded="mobileMenu"
              @click="toggleMenu"
            >
              <svg class="h-5 w-5" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="!mobileMenu" d="M4 6h16M4 12h16"></path>
                <path v-else d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div v-show="mobileMenu" class="border-t border-black/10 px-3 pb-3 pt-2 md:hidden">
          <a
            v-for="nav in navs"
            :key="nav.id"
            @click="scrollToView(nav.name)"
            class="block cursor-pointer rounded-2xl px-4 py-3 text-sm font-semibold uppercase tracking-[0.16em] transition"
            :class="path === nav.id ? 'bg-[#23362f] text-[#f7f2e8]' : 'text-[#2f3835] hover:bg-white/75'"
            :aria-current="path === nav.id ? 'page' : undefined"
          >
            {{ nav.label }}
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { onMounted, onUnmounted, ref } from "vue";
import { useStorageFile } from "@/composables/useStorageAssets";
import { storageFiles } from "@/config/storageSections";

export default {
  setup() {
    const navs = ref([
      { id: 1, label: "Home", name: "home", url: "#" },
      { id: 2, label: "Story", name: "about", url: "#" },
      { id: 3, label: "Work", name: "projects", url: "#" },
      { id: 4, label: "Contact", name: "contact_us", url: "#" },
    ]);

    const mobileMenu = ref(false);
    const path = ref(1);
    const isScrolled = ref(false);
    const { assetUrl: logoIcon } = useStorageFile(storageFiles.logoIcon);
    const { assetUrl: logoWordmark } = useStorageFile(storageFiles.logoWordmark);

    const toggleMenu = () => {
      mobileMenu.value = !mobileMenu.value;
    };

    const scrollToView = (id) => {
      mobileMenu.value = false;
      let el = document.getElementById(id);
      if (!el) {
        return;
      }

      const headerOffset = 88;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollBy({
        top: offsetPosition - window.pageYOffset,
        behavior: "smooth",
      });
    };

    const updateNavigationState = () => {
      isScrolled.value = window.scrollY > 24;

      const activeNav = [...navs.value].reverse().find((nav) => {
        const section = document.getElementById(nav.name);
        return section && window.scrollY >= section.offsetTop - 160;
      });

      path.value = activeNav?.id || 1;
    };

    onMounted(() => {
      updateNavigationState();
      window.addEventListener("scroll", updateNavigationState, { passive: true });
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", updateNavigationState);
    });

    return {
      isScrolled,
      path,
      navs,
      logoIcon,
      logoWordmark,
      toggleMenu,
      mobileMenu,
      scrollToView,
    };
  },
};
</script>
<style lang="">
</style>
